
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sharedApi } from '../../api/sharedApi';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Loader2, ArrowRight, Building } from 'lucide-react';
import { useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'sonner';

export const SharedLawFirmsList = () => {
    const { token, isCompany } = useAuth();
    const navigate = useNavigate();

    // Hooks unconditional
    const [firms, setFirms] = useState([]);
    const [loading, setLoading] = useState(true);

    const isCompanyUser = isCompany();

    const fetchFirms = async () => {
        try {
            const data = await sharedApi.getLawFirms(token);
            setFirms(data);
        } catch (error) {
            console.error(error);
            toast.error("Error al cargar despachos vinculados");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isCompanyUser) return;
        fetchFirms();
    }, [token, isCompanyUser]);

    // Gating AFTER hooks
    if (!isCompanyUser) {
        return <Navigate to="/dashboard" replace />;
    }

    if (loading) {
        return (
            <>
                <div className="flex justify-center items-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
            </>
        );
    }

    return (
        <>
            <div className="space-y-6">
                <div>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">Despachos Vinculados</h1>
                    <p className="text-muted-foreground">Acceso compartido a información de tus proveedores legales.</p>
                </div>

                {firms.length === 0 ? (
                    <Card>
                        <CardContent className="py-10 text-center text-gray-500">
                            <Building className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                            <p>No tienes despachos vinculados actualmente.</p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {firms.map(firm => (
                            <Card key={firm.tenant_id} className="hover:shadow-md transition-shadow">
                                <CardHeader className="pb-2">
                                    <CardTitle className="flex items-center gap-2">
                                        <Building className="h-5 w-5 text-indigo-600" />
                                        {firm.name}
                                    </CardTitle>
                                    <CardDescription>ID: {firm.tenant_id.substring(0, 8)}...</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="mb-4">
                                        <div className="text-xs font-semibold text-gray-500 mb-2 uppercase">Accesos Habilitados:</div>
                                        <div className="flex flex-wrap gap-2">
                                            {Object.entries(firm.scopes).map(([key, enabled]) =>
                                                enabled ? (
                                                    <Badge key={key} variant="secondary" className="text-xs">
                                                        {key.replace('share_', '').replace('_', ' ')}
                                                    </Badge>
                                                ) : null
                                            )}
                                            {/* Show "Ninguno" if no scopes enabled */}
                                            {Object.values(firm.scopes).every(v => !v) && <span className="text-sm text-gray-400">Sin accesos configurados</span>}
                                        </div>
                                    </div>
                                    <Button className="w-full" onClick={() => navigate(`/shared/lawfirms/${firm.tenant_id}`)}>
                                        Ver Información <ArrowRight className="ml-2 h-4 w-4" />
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </>
    );
};
